// src/components/SharedFileViewer.js
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../supabase';
import bcrypt from 'bcryptjs';
import { FaLock, FaRegFilePdf, FaExclamationTriangle, FaHourglassEnd, FaDownload } from 'react-icons/fa';
import { PreviewContent } from '../modals/PreviewModal';
import { decryptText } from '../../utils/crypto';
import './SharedFileViewer.css';

export const SharedFileViewer = () => {
  const { linkId } = useParams();
  const [linkData, setLinkData] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | password | ready | expired | error
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [shakeError, setShakeError] = useState(false);

  useEffect(() => {
    const fetchLink = async () => {
      const { data, error } = await supabase
        .from('sharedLinks')
        .select('*')
        .eq('id', linkId)
        .single();

      if (error || !data) {
        setStatus('error');
        return;
      }

      // Check expiry
      if (new Date(data.expiresAt) < new Date()) {
        setStatus('expired');
        return;
      }

      setLinkData(data);

      if (data.passwordHash) {
        setStatus('password');
      } else {
        setStatus('ready');
      }
    };

    if (linkId) fetchLink();
  }, [linkId]);

  // Live Security Polling: Auto-shutoff preview immediately upon link expiration
  useEffect(() => {
    if (!linkData) return;

    const checkInterval = setInterval(() => {
      const isExpired = new Date(linkData.expiresAt) < new Date();
      if (isExpired) {
        setStatus('expired');
      }
    }, 1000);

    return () => clearInterval(checkInterval);
  }, [linkData]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      if (bcrypt.compareSync(password, linkData.passwordHash)) {
        const decrypted = await decryptText(linkData.originalUrl, password);
        setLinkData(prev => ({ ...prev, originalUrl: decrypted }));
        setStatus('ready');
        setPasswordError('');
        setShakeError(false);
      } else {
        throw new Error("Incorrect passcode. Please try again.");
      }
    } catch (err) {
      setPasswordError(err.message || 'Incorrect passcode. Please try again.');
      setShakeError(true);
      setTimeout(() => setShakeError(false), 500);
    }
  };

  if (status === 'loading') {
    return (
      <div className="shared-viewer-layout">
        <div className="status-card-container">
          <p className="loading-text">Verifying secure workspace link...</p>
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="shared-viewer-layout">
        <div className="status-card-container">
          <div className="status-card">
            <div className="status-card-icon expired"><FaHourglassEnd /></div>
            <h2>Shared Link Expired</h2>
            <p>This shared file access link has reached its expiration limit and is no longer available.</p>
            <Link to="/" className="status-home-btn">Return to MyDrive</Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="shared-viewer-layout">
        <div className="status-card-container">
          <div className="status-card">
            <div className="status-card-icon error"><FaExclamationTriangle /></div>
            <h2>Workspace Link Broken</h2>
            <p>This shared link is invalid, broken, or has been revoked by the workspace owner.</p>
            <Link to="/" className="status-home-btn">Return to MyDrive</Link>
          </div>
        </div>
      </div>
    );
  }

  if (status === 'password') {
    return (
      <div className="shared-viewer-layout">
        <div className="password-screen-container">
          <div className={`glass-lock-card ${shakeError ? 'shake-error' : ''}`}>
            <div className="lock-icon-wrapper">
              <FaLock />
            </div>
            <h2>Secure Workspace Vault</h2>
            <p>Please enter the decryption passcode configured by the owner to unlock this file preview.</p>
            <form onSubmit={handlePasswordSubmit} className="glass-lock-form">
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Passcode Required"
                className="glass-pass-input"
                autoFocus
              />
              {passwordError && <p className="error-message">{passwordError}</p>}
              <button type="submit" className="unlock-btn">
                Decrypt & Unlock
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="shared-viewer-layout">
      <div className="shared-workspace">
        <div className="shared-header-panel">
          <div className="shared-meta-info">
            <h2 className="shared-meta-title">
              <FaRegFilePdf className="pdf-icon" />
              <span>{linkData.fileName}</span>
              <span className="secure-badge">● Secure Prism Share</span>
            </h2>
          </div>
          {linkData.allowDownload && (
            <a href={linkData.originalUrl} download={linkData.fileName} className="download-link-btn">
              <FaDownload /> Download Document
            </a>
          )}
        </div>
        <div className="preview-frame-wrapper">
          <PreviewContent
            file={{ fileName: linkData.fileName, fileUrl: linkData.originalUrl }}
            allowDownload={linkData.allowDownload}
          />
        </div>
      </div>
    </div>
  );
};